import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'

import { Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { getVendorUsers, IVendorUser } from '@/core/services/vendors'
import { CreateUserModal } from '@/home/components/vendors/CreateUserModal'
import Loader from '@/common/components/Loader'

interface UsersTabProps {
  vendorId: string
  onError: (message: string) => void
  createModalOpen: boolean
  onCreateModalChange: (open: boolean) => void
}

export const UsersTab = ({
  vendorId,
  onError,
  createModalOpen,
  onCreateModalChange,
}: UsersTabProps) => {
  const [users, setUsers] = useState<IVendorUser[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [editUser, setEditUser] = useState<IVendorUser | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const fetchUsers = useCallback(async () => {
    if (!vendorId) return
    setLoading(true)
    try {
      const { data } = await getVendorUsers(vendorId)
      setUsers(data)
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck(!isCheckAll ? users.map((u) => u.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    setIsCheck(checked ? [...isCheck, id] : isCheck.filter((i) => i !== id))
  }

  const handleNameClick = (user: IVendorUser) => {
    setEditUser(user)
    setEditModalOpen(true)
  }

  const handleEditModalChange = (open: boolean) => {
    setEditModalOpen(open)
    if (!open) setEditUser(null)
  }

  const handleSuccess = () => {
    setIsCheck([])
    setIsCheckAll(false)
    fetchUsers()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row: IVendorUser) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row: IVendorUser) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleNameClick(row)
          }}
        >
          {row.name}
        </a>
      ),
    },
    { title: 'Phone', accessor: 'phone', sortable: true },
    {
      title: '',
      accessor: '',
      render: (row: IVendorUser) =>
        isCheck.includes(row.id) ? (
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-monochrome-5/70 text-monochrome-1 hover:bg-red-500/30 hover:text-red-400 transition-colors cursor-pointer"
            onClick={() => handleNameClick(row)}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null,
    },
  ]

  return (
    <>
      {loading && <Loader />}
      {users.length ? (
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={users}
          isCheck={isCheck}
        />
      ) : (
        <div className="flex items-center justify-center py-20 text-monochrome-2">
          No Users data available
        </div>
      )}

      <CreateUserModal
        open={createModalOpen}
        onOpenChange={onCreateModalChange}
        vendorId={vendorId}
        onSuccess={handleSuccess}
        onError={onError}
      />

      <CreateUserModal
        open={editModalOpen}
        onOpenChange={handleEditModalChange}
        vendorId={vendorId}
        user={editUser}
        onSuccess={handleSuccess}
        onError={onError}
      />
    </>
  )
}
